/**
 * OIDC Authentication Provider
 * Handles authentication via OpenID Connect (Keycloak)
 */

import type { AuthProvider, UserContext } from '../types';
import { getUserManager } from '../../oidc-client';
import type { UserManager } from 'oidc-client-ts';
import { BaseAuthProvider } from './BaseAuthProvider';
import { apiFetch } from '../../ultra-meet-config';
import { buildDisplayName } from '../../runtime-config';

export class OidcAuthProvider extends BaseAuthProvider implements AuthProvider {
    readonly name = 'oidc' as const;

    private userManager: UserManager | null = null;

    async isAuthenticated(): Promise<boolean> {
        // Always verify the OIDC session has a valid, non-expired token
        // Don't rely solely on localStorage cache — it can be stale
        try {
            const um = await this.getUserManager();
            const user = await um.getUser();

            if (user !== null && !user.expired && user.access_token) {
                return true;
            }

            // OIDC session is invalid/expired — clear stale cache if present
            const cached = this.getStoredUser();
            if (cached) {
                this.clearStorage();
            }

            return false;
        } catch (error) {
            console.error('Failed to check OIDC authentication:', error);
            return false;
        }
    }

    async getCurrentUser(): Promise<UserContext | null> {
        // Always refresh OIDC user data from the API when possible. The backend
        // can normalize identity/name fields per environment, so cached values
        // must not permanently hide config changes (for example first-name-only
        // Keycloak realms).
        const cached = this.getStoredUser();

        try {
            const token = await this.getAccessToken();
            if (!token) return null;

            const response = await apiFetch('/api/v1/user/me', {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (!response.ok) {
                return response.status === 401 ? null : cached;
            }

            const data = await response.json();

            if (data.result !== 'success' || !data.data) {
                return cached;
            }

            const user: UserContext = {
                id: data.data.uuid,
                email: data.data.email,
                firstName: data.data.first_name,
                lastName: data.data.last_name,
                displayName: buildDisplayName(data.data.first_name, data.data.last_name, data.data.email),
                photoUrl: data.data.photo_url || ''
            };

            this.storeUser(user);

            return user;
        } catch (error) {
            console.error('Failed to get current user:', error);
            return cached;
        }
    }

    async signIn(): Promise<void> {
        const um = await this.getUserManager();
        await um.signinRedirect();
    }

    async signOut(): Promise<void> {
        this.clearStorage();

        try {
            const um = await this.getUserManager();
            const user = await um.getUser();

            // Remove local user state first
            await um.removeUser();

            // End session on Keycloak without confirmation page
            // id_token_hint tells Keycloak which session to terminate
            // post_logout_redirect_uri redirects back without showing confirmation
            if (user?.id_token) {
                await um.signoutRedirect({
                    id_token_hint: user.id_token,
                    post_logout_redirect_uri: window.location.origin
                });
                return;
            }
        } catch (error) {
            console.error('Failed to sign out from Keycloak:', error);
        }

        // Fallback: redirect to home page
        if (typeof window !== 'undefined') {
            window.location.href = '/';
        }
    }

    async refreshTokens(): Promise<void> {
        try {
            const um = await this.getUserManager();
            await um.signinSilent();
        } catch (error) {
            console.error('Failed to refresh OIDC tokens:', error);
            throw error;
        }
    }

    async getAccessToken(): Promise<string | null> {
        try {
            const um = await this.getUserManager();
            const user = await um.getUser();

            if (user && !user.expired && user.access_token) {
                return user.access_token;
            }

            return null;
        } catch (error) {
            console.error('Failed to get access token:', error);
            return null;
        }
    }

    private async getUserManager(): Promise<UserManager> {
        if (!this.userManager) {
            this.userManager = await getUserManager();
        }
        return this.userManager;
    }
}
